import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import withMountGuard from 'hocs/withMountGuard'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyTierBenefitMessageBanner from 'loyalty/components/LoyaltyTierBenefitMessageBanner'
import LoyaltyStayUpgradeDetailsModal, { LoyaltyStayUpgradeDetailsModalResult } from 'loyalty/components/StayUpgrade/LoyaltyStayUpgradeDetailsModal'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import TextLink from 'LuxKit/TextLink'
import React, { useCallback, useContext } from 'react'
import OfferPageLoyaltyStayUpgradeCallOutBannerAction from './OfferPageLoyaltyStayUpgradeCallOutBannerAction'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import * as Analytics from 'analytics/analytics'
import styled from 'styled-components'
import { rem } from 'polished'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import Typography from 'LuxKit/Typography/Typography'

/**
 * @NOTE typically you do NOT want to do this but this is a single one-off exception.
 * An AB test has forced a rejig of <LoyaltyTierMessageBanner />. A proper DS solution is required.
 */
const MobileLoyaltyTierBenefitMessageBanner = styled(LoyaltyTierBenefitMessageBanner)`
  padding-inline: ${rem(20)} ${rem(12)} !important;
  min-height: ${rem(56)} !important;
`

/**
 * @NOTE we don't want to usually want to create custom breakpoints like this but product
 * have specifically requested the copy below and it only fits on mid mobile sizes up.
 */
const ShowOnlySmallMobileBreakpoint = styled.div`
  @media screen and (min-width: 405px) {
    display: none;
  }
`

const ShowMidMobileUpBreakpoint = styled.div`
  @media screen and (max-width: 404.98px) {
    display: none;
  }
`

interface Props {
  packageView: App.OfferPackageOptionView
}

function OfferPageLoyaltyStayUpgradeCallOutBanner({ packageView }: Props) {
  const loyaltyUpgradeAvailability = packageView.loyaltyUpgradeAvailability
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const showModal = useContext(ModalContext)
  const { offer, checkInDate, checkOutDate } = useContext(OfferPageStateContext)
  const isHorizontalSliderEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.croHorizontalScrollFlashPackages)
  const { selectedTargetUpgradePackage } = useLoyaltyOfferPageTargetStayUpgrade(packageView.package)

  const { toggleTargetStayUpgrade, selectedTargetUpgradeType } = useLoyaltyOfferPageTargetStayUpgrade(packageView.package)
  const applyUpgrade = useCallback((upgradeType: App.LoyaltyStayUpgradeType | undefined) => {
    toggleTargetStayUpgrade(upgradeType)
    Analytics.trackClientEvent({
      subject: `loyalty_${upgradeType}_upgrade`,
      action: upgradeType ? 'toggled_on' : 'toggled_off',
      category: 'offer_page',
      type: 'interaction',
    })
  }, [toggleTargetStayUpgrade])

  const showTargetUpgradeRoomDetails = useCallback(async() => {
    let result: LoyaltyStayUpgradeDetailsModalResult | undefined

    switch (loyaltyUpgradeAvailability?.upgradeType) {
      case 'room-only':
        result = await showModal(<LoyaltyStayUpgradeDetailsModal
          upgradeType="room"
          originPackage={packageView.package}
          targetPackage={loyaltyUpgradeAvailability.roomUpgradePackage}
          offer={offer}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          hasApplyAction
          hasRemoveAction={selectedTargetUpgradeType === 'room'}
        />)

        break
      case 'package-only':
        result = await showModal(<LoyaltyStayUpgradeDetailsModal
          upgradeType="package"
          originPackage={packageView.package}
          targetPackage={loyaltyUpgradeAvailability.packageUpgradePackage}
          offer={offer}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          hasApplyAction
          hasRemoveAction={selectedTargetUpgradeType === 'package'}
        />)

        break
    }

    if (result) {
      applyUpgrade(result.upgradeType)
    }
  }, [applyUpgrade, checkInDate, checkOutDate, loyaltyUpgradeAvailability, offer, packageView, selectedTargetUpgradeType, showModal])

  if (!loyaltyUpgradeAvailability) return null

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <CSSBreakpoint only="mobile">
      {isHorizontalSliderEnabled && <MobileLoyaltyTierBenefitMessageBanner
        tier={tier}
        benefitType="hotel_upgrade"
        endContent={<OfferPageLoyaltyStayUpgradeCallOutBannerAction packageView={packageView} />}
        density="compact"
        icon={false}
      >
        <Group direction="vertical" wrap="wrap">
          {loyaltyUpgradeAvailability.upgradeType === 'room-and-package' && !selectedTargetUpgradePackage && <>Free upgrade available</>}
          {loyaltyUpgradeAvailability.upgradeType === 'room-and-package' && !!selectedTargetUpgradePackage && <>Free upgrade applied!</>}
          <ShowOnlySmallMobileBreakpoint>
            {loyaltyUpgradeAvailability.upgradeType === 'room-only' && <>
              <Typography wrap="no-wrap">Free room upgrade</Typography>
              <TextLink weight="regular" size="caption" onClick={showTargetUpgradeRoomDetails}>View upgraded room</TextLink>
            </>}
            {loyaltyUpgradeAvailability.upgradeType === 'package-only' && <>
              <Typography wrap="no-wrap">Free package upgrade</Typography>
              <TextLink weight="regular" size="caption" onClick={showTargetUpgradeRoomDetails}>View upgraded inclusions</TextLink>
            </>}
          </ShowOnlySmallMobileBreakpoint>
          <ShowMidMobileUpBreakpoint>
            {loyaltyUpgradeAvailability.upgradeType === 'room-only' && <>
              <Typography wrap="no-wrap">Apply free room upgrade</Typography>
              <TextLink weight="regular" size="caption" onClick={showTargetUpgradeRoomDetails}>View upgraded room</TextLink>
            </>}
            {loyaltyUpgradeAvailability.upgradeType === 'package-only' && <>
              <Typography wrap="no-wrap">Apply free package upgrade</Typography>
              <TextLink weight="regular" size="caption" onClick={showTargetUpgradeRoomDetails}>View upgraded inclusions</TextLink>
            </>}
          </ShowMidMobileUpBreakpoint>
        </Group>
      </MobileLoyaltyTierBenefitMessageBanner>}
      {!isHorizontalSliderEnabled && <LoyaltyTierBenefitMessageBanner
        tier={tier}
        benefitType="hotel_upgrade"
        endContent={<OfferPageLoyaltyStayUpgradeCallOutBannerAction packageView={packageView} />}
      >
        <Group direction="vertical" wrap="wrap">
          {loyaltyUpgradeAvailability.upgradeType === 'room-and-package' && !selectedTargetUpgradePackage && <>Free upgrade available</>}
          {loyaltyUpgradeAvailability.upgradeType === 'room-and-package' && !!selectedTargetUpgradePackage && <>Free upgrade applied!</>}
          {loyaltyUpgradeAvailability.upgradeType === 'room-only' && <>
            <div>Apply free room upgrade</div>
            <TextLink size="caption" onClick={showTargetUpgradeRoomDetails}>View upgraded room</TextLink>
          </>}
          {loyaltyUpgradeAvailability.upgradeType === 'package-only' && <>
            <div>Apply free package upgrade</div>
            <TextLink size="caption" onClick={showTargetUpgradeRoomDetails}>View upgraded inclusions</TextLink>
          </>}
        </Group>
      </LoyaltyTierBenefitMessageBanner>}
    </CSSBreakpoint>
    <CSSBreakpoint min="tablet">
      <LoyaltyTierBenefitMessageBanner
        tier={tier}
        benefitType="hotel_upgrade"
        endContent={<OfferPageLoyaltyStayUpgradeCallOutBannerAction packageView={packageView} />}
      >
        <Group direction="horizontal" gap="0 24" wrap="wrap">
          {loyaltyUpgradeAvailability.upgradeType === 'room-and-package' && <div>Free room or package upgrade</div>}
          {loyaltyUpgradeAvailability.upgradeType === 'room-only' && <>
            <Group direction="horizontal" gap={8}>
              <div>Apply free room upgrade to</div>
              <b>{loyaltyUpgradeAvailability.roomUpgradePackage.roomType?.name ?? loyaltyUpgradeAvailability.roomUpgradePackage.name}</b>
            </Group>
            <TextLink size="caption" onClick={showTargetUpgradeRoomDetails}>View upgraded room</TextLink>
          </>}
          {loyaltyUpgradeAvailability.upgradeType === 'package-only' && <>
            <div>Apply free package upgrade</div>
            <TextLink size="caption" onClick={showTargetUpgradeRoomDetails}>View upgraded inclusions</TextLink>
          </>}
        </Group>
      </LoyaltyTierBenefitMessageBanner>
    </CSSBreakpoint>
  </ProductPaletteProvider>
}

export default withMountGuard(() => useAppSelector(getIsCurrentRegionUserLoyaltyRegion))(
  withOfferPageState((state) => ({
    offer: state.offer,
  }), OfferPageLoyaltyStayUpgradeCallOutBanner),
)
